import { useState, useCallback, useRef } from 'react';
import { usePostMessage } from '../PostMessageContext';
import { useAuth } from '../AuthContext';
import { detectIframe } from '../iframeUtils';
import api from '../api';

// ── Field mapping: parent ↔ portal ──
// Parent uses flat details with field_order array; portal uses nested fields object with order numbers

export function parentToPortalFields(details) {
  if (!details) return {};
  const fieldOrder = details.field_order || [];
  const fieldKeys = ['gender','dob','smoker','nicotine','coverage','term','health','name','email','phone','type','state','zip','height_weight'];
  const mapped = {};
  // Map 'dob' → 'age', 'nicotine' → 'smoker', 'height_weight' → split into height+weight
  const keyMap = { dob: 'age', nicotine: 'smoker', height_weight: 'height' };

  fieldKeys.forEach((pk, fallbackIdx) => {
    const fieldData = details[pk];
    if (!fieldData || typeof fieldData !== 'object') return;
    const portalKey = keyMap[pk] || pk;
    const orderIdx = fieldOrder.indexOf(pk);
    mapped[portalKey] = {
      active: fieldData.active !== false,
      label: (fieldData.label || portalKey).replace(/:$/, ''),
      order: orderIdx >= 0 ? orderIdx + 1 : fallbackIdx + 1,
      ...(fieldData.values ? { values: fieldData.values } : {}),
      ...(fieldData.separate_first_last !== undefined ? { separate_first_last: fieldData.separate_first_last } : {}),
    };
  });
  // Add weight from height_weight if present
  if (details.height_weight && typeof details.height_weight === 'object') {
    const hwOrder = fieldOrder.indexOf('height_weight');
    mapped.weight = {
      active: details.height_weight.active !== false,
      label: 'Weight',
      order: (hwOrder >= 0 ? hwOrder + 1 : 50) + 0.5,
    };
  }
  return mapped;
}

export function portalToParentFields(fields) {
  if (!fields) return {};
  const result = {};
  const reverseMap = { age: 'dob', smoker: 'nicotine' };
  const sortedKeys = Object.entries(fields).sort((a, b) => a[1].order - b[1].order);
  const fieldOrder = [];

  sortedKeys.forEach(([key, data]) => {
    if (key === 'weight') return; // Merged into height_weight
    const parentKey = reverseMap[key] || key;
    // Merge height + weight back into height_weight
    if (key === 'height') {
      const weightData = fields.weight;
      result.height_weight = {
        label: data.label + ':',
        active: data.active,
      };
      fieldOrder.push('height_weight');
      return;
    }
    result[parentKey] = {
      label: data.label + ':',
      active: data.active,
      ...(data.values ? { values: data.values } : {}),
      ...(data.separate_first_last !== undefined ? { separate_first_last: data.separate_first_last } : {}),
    };
    fieldOrder.push(parentKey);
  });
  result.field_order = fieldOrder;
  return result;
}

export function parentToPortalWidget(pw) {
  const d = pw.details || {};
  return {
    id: pw.id || pw._id,
    key: pw.key,
    title: pw.title || '',
    engine_id: pw.engine_id || d.type?.values?.[0]?.toString() || '1',
    engine_title: pw.engine_title || '',
    details: {
      // V2 keys pass through directly
      template: d.template || d.version === '2' ? (d.template || 'modern') : 'modern',
      header_style: d.header_style === '1' ? 'curve' : d.header_style === '2' ? 'curve' : (d.header_style || 'curve'),
      compact: d.compressed_layout ?? d.compact ?? true,
      primary_color: d.container_border_color || d.primary_color || '#1E3A5F',
      accent_color: d.cta_bg_color || d.accent_color || '#FF7F22',
      header_text_color: d.title_text_color || d.header_text_color || '',
      label_text_color: d.label_text_color || '',
      cta_text_color: d.cta_text_color || '',
      font: d.font || 'system',
      eyebrow: d.eyebrow || '',
      heading: d.title || d.heading || '',
      subtitle: d.subtitle || '',
      cta_text: d.cta_text || 'Continue',
      container_shadow: d.container_shadow !== false,
      disclaimer: d.disclaimer_text || d.disclaimer || '',
      show_logo_scroller: d.show_logo_scroller !== false,
      show_logo: d.show_logo || false,
      ab_group: d.ab_group || '',
      ab_notes: d.ab_notes || '',
      disclaimer_checkbox: d.disclaimer_checkbox || '',
      disclaimer_visibility_override: d.disclaimer_visibility_override || '',
      default_coverage_amount: d.default_coverage_amount || '',
      fields: parentToPortalFields(d),
    },
  };
}

export function portalToParentDetails(details) {
  const fieldData = portalToParentFields(details.fields);
  return {
    // Preserve V2 marker
    version: '2',
    template: details.template,
    header_style: details.header_style,
    compressed_layout: details.compact,
    compact: details.compact,
    // Color mapping — send both old and new keys
    primary_color: details.primary_color,
    container_border_color: details.primary_color,
    accent_color: details.accent_color,
    cta_bg_color: details.accent_color,
    container_bg_color: '#FFFFFF',
    // Text colors
    eyebrow_text_color: details.header_text_color || '',
    title_text_color: details.header_text_color || '',
    subtitle_text_color: details.header_text_color || '',
    header_text_color: details.header_text_color || '',
    label_text_color: details.label_text_color || '',
    selected_option_color: details.primary_color,
    selected_option_text_color: '#FFFFFF',
    cta_text_color: details.cta_text_color || '',
    // Text
    eyebrow: details.eyebrow || '',
    title: details.heading || '',
    heading: details.heading || '',
    subtitle: details.subtitle || '',
    cta_text: details.cta_text || 'Continue',
    container_shadow: details.container_shadow,
    // Disclaimer
    disclaimer_text: details.disclaimer || '',
    disclaimer: details.disclaimer || '',
    disclaimer_checkbox: details.disclaimer_checkbox || '',
    disclaimer_visibility_override: details.disclaimer_visibility_override || '',
    // V2 extras
    font: details.font || 'system',
    show_logo_scroller: details.show_logo_scroller,
    show_logo: details.show_logo,
    ab_group: details.ab_group || '',
    ab_notes: details.ab_notes || '',
    default_coverage_amount: details.default_coverage_amount || '',
    // Fields (flattened for parent)
    ...fieldData,
  };
}

// ── Hook: useWidgetData ──
export function useWidgetData() {
  const { brand, isIframe } = useAuth();
  const postMessage = usePostMessage();
  const pmRef = useRef(postMessage);
  pmRef.current = postMessage;
  // Use the synchronous detectIframe() result — NOT the AuthContext state,
  // which only flips true AFTER the parent's `brand` message arrives.
  // Otherwise fetchWidgets races ahead of parent handshake and hits the local
  // backend, masking real parent failures.
  const inIframe = detectIframe();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWidgets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (inIframe && pmRef.current?.getWidgets) {
        const parentWidgets = await pmRef.current.getWidgets();
        setWidgets((parentWidgets || []).map(parentToPortalWidget));
      } else {
        const res = await api.get('/widgets');
        setWidgets(res.data.widgets || []);
      }
    } catch (e) {
      console.error('Failed to fetch widgets:', e);
      if (inIframe) {
        // In iframe mode, do NOT silently fall back to the local DB — surface the error
        // so the embedding parent can be diagnosed and the user isn't stuck spinning.
        setError("We couldn't load your widgets from the host application. This usually means the host page isn't responding. Please refresh the page — if the issue continues, contact support.");
        setWidgets([]);
      } else {
        setError('Unable to load widgets. Please refresh or try again.');
        setWidgets([]);
      }
    }
    setLoading(false);
  }, [inIframe]);

  const saveWidget = useCallback(async (widget, formData) => {
    const { title, engine_id, details } = formData;
    if (inIframe && pmRef.current?.saveWidgetToParent) {
      const parentDetails = portalToParentDetails(details);
      const res = await pmRef.current.saveWidgetToParent({
        key: widget.key || 'new',
        title,
        engine_id,
        details: parentDetails,
      });
      const payload = res?.payload || res;
      if (payload?.key && payload.key !== 'new') {
        return { ...widget, key: payload.key, title, engine_id, details, ...(payload.id ? { id: payload.id } : {}) };
      }
      return { ...widget, title, engine_id, details };
    } else {
      const endpoint = widget.id ? `/widgets/${widget.id}` : '/widgets';
      const method = widget.id ? 'put' : 'post';
      const res = await api[method](endpoint, { title, engine_id, details });
      return res.data.widget;
    }
  }, [inIframe]);

  const createWidget = useCallback(async (title, engineId) => {
    if (inIframe && pmRef.current?.saveWidgetToParent) {
      const res = await pmRef.current.saveWidgetToParent({
        key: 'new',
        title,
        engine_id: engineId,
        details: { version: '2', template: 'modern', header_style: 'curve', compact: true },
      });
      const payload = res?.payload || res;
      return { id: payload?.id, key: payload?.key || 'new', title, engine_id: engineId, engine_title: '', details: {} };
    } else {
      const res = await api.post('/widgets', { title, engine_id: engineId });
      return res.data.widget;
    }
  }, [inIframe]);

  const duplicateWidget = useCallback(async (widget) => {
    if (inIframe && pmRef.current?.saveWidgetToParent) {
      const parentDetails = portalToParentDetails(widget.details || {});
      const res = await pmRef.current.saveWidgetToParent({
        key: 'new',
        title: (widget.title || '') + ' (Copy)',
        engine_id: widget.engine_id,
        details: parentDetails,
      });
      const payload = res?.payload || res;
      return { ...widget, id: payload?.id, key: payload?.key || 'new', title: (widget.title || '') + ' (Copy)' };
    } else {
      if (widget.id) {
        const res = await api.post(`/widgets/${widget.id}/duplicate`);
        return res.data.widget;
      }
      return null;
    }
  }, [inIframe]);

  return { widgets, setWidgets, loading, error, fetchWidgets, saveWidget, createWidget, duplicateWidget, isIframe: inIframe };
}
